import React, { useState, createContext, useContext, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getNotifications, getUnreadNotificationCount, markNotificationAsRead, markAllNotificationsAsRead } from '../services/api';

// Create context for sidebar state
const SidebarContext = createContext();

export const useSidebar = () => useContext(SidebarContext);

function AdminLayout({ children, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Initialize sidebar state from localStorage or default to true (open) on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('admin_sidebar_open');
      return saved !== null ? saved === 'true' : true; // Default to open
    }
    return true;
  });

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('admin_sidebar_open', sidebarOpen.toString());
    }
  }, [sidebarOpen]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuButtonRef = useRef(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationsButtonRef = useRef(null);
  const notificationsIntervalRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const audioContextRef = useRef(null); // Persistent audio context
  const previousUnreadCountRef = useRef(-1); // Use -1 as initial state to distinguish from 0

  // Check if user is a courier
  const isCourier = user?.role?.name === 'courier';
  const isSuperAdmin = user?.role?.name === 'super_admin';
  
  // Base menu items
  const allMenuItems = [
    { path: '/admin', label: 'Dashboard', icon: '📊' },
    { path: '/admin/products', label: 'Products', icon: '🛍️' },
    { path: '/admin/categories', label: 'Categories', icon: '📁' },
    { path: '/admin/orders', label: 'Orders', icon: '📦' },
    { path: '/admin/unprocessed-payments', label: 'Unprocessed', icon: '⚠️', superAdminOnly: true },
    { path: '/admin/transactions', label: 'Transactions', icon: '💰', superAdminOnly: true },
    { path: '/admin/users', label: 'Users', icon: '👥' },
    { path: '/admin/courier', label: 'Courier', icon: '🚚' },
    { path: '/admin/support', label: 'Support', icon: '💬' },
    { path: '/admin/settings', label: 'Settings', icon: '⚙️' },
  ];
  
  // Filter menu items based on user role
  const menuItems = isCourier 
    ? allMenuItems.filter(item => item.path === '/admin/courier')
    : allMenuItems.filter(item => {
        // Hide superAdminOnly items if user is not a super admin
        if (item.superAdminOnly && !isSuperAdmin) {
          return false;
        }
        return true;
      });

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const data = await getNotifications(token);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [user]);

  // Initialize notification sound
  useEffect(() => {
    // Initialize persistent audio context if available
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass && !audioContextRef.current) {
        audioContextRef.current = new AudioContextClass();
        // Try to resume if suspended (unlock on user interaction)
        if (audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume().catch(err => {
            console.warn('[AdminLayout] Could not resume audio context on init:', err);
          });
        }
        console.log('[AdminLayout] Audio context initialized, state:', audioContextRef.current.state);
      }
    } catch (error) {
      console.warn('[AdminLayout] Could not create audio context:', error);
    }

    // Create a longer, more noticeable notification sound using Web Audio API
    // This creates a pleasant two-tone chime sound without requiring an external file
    const playNotificationSound = async () => {
      console.log('[AdminLayout] playNotificationSound called');
      try {
        let audioContext = audioContextRef.current;
        
        // Create new context if we don't have one
        if (!audioContext) {
          const AudioContextClass = window.AudioContext || window.webkitAudioContext;
          if (!AudioContextClass) {
            throw new Error('Web Audio API not supported');
          }
          audioContext = new AudioContextClass();
          audioContextRef.current = audioContext;
        }
        
        // Resume AudioContext if suspended (required by some browsers)
        if (audioContext.state === 'suspended') {
          console.log('[AdminLayout] AudioContext suspended, resuming...');
          await audioContext.resume();
          console.log('[AdminLayout] AudioContext resumed, state:', audioContext.state);
        }
        
        const duration = 0.8; // 800ms - longer and more noticeable
        const now = audioContext.currentTime;
        
        // Create two oscillators for a chime effect (two-tone notification)
        const oscillator1 = audioContext.createOscillator();
        const oscillator2 = audioContext.createOscillator();
        const gainNode1 = audioContext.createGain();
        const gainNode2 = audioContext.createGain();
        const masterGain = audioContext.createGain();
        
        // Connect oscillators to their gain nodes
        oscillator1.connect(gainNode1);
        oscillator2.connect(gainNode2);
        gainNode1.connect(masterGain);
        gainNode2.connect(masterGain);
        masterGain.connect(audioContext.destination);
        
        // First tone: 880Hz (A5 note) - more musical
        oscillator1.frequency.value = 880;
        oscillator1.type = 'sine';
        
        // Second tone: 1320Hz (E6 note) - harmonic interval
        oscillator2.frequency.value = 1320;
        oscillator2.type = 'sine';
        
        // Set volume envelopes for both tones
        // First tone: starts immediately, fades out
        gainNode1.gain.setValueAtTime(0.4, now);
        gainNode1.gain.exponentialRampToValueAtTime(0.01, now + duration * 0.6);
        
        // Second tone: starts slightly delayed for chime effect
        gainNode2.gain.setValueAtTime(0, now);
        gainNode2.gain.setValueAtTime(0.4, now + 0.15); // Starts after 150ms
        gainNode2.gain.exponentialRampToValueAtTime(0.01, now + duration);
        
        // Master volume
        masterGain.gain.setValueAtTime(0.5, now);
        
        // Play the sound
        console.log('[AdminLayout] Starting oscillators');
        oscillator1.start(now);
        oscillator1.stop(now + duration);
        oscillator2.start(now);
        oscillator2.stop(now + duration);
        console.log('[AdminLayout] Notification sound playing');
      } catch (error) {
        console.error('[AdminLayout] Web Audio API error:', error);
        // Fallback: try HTML5 Audio API if Web Audio API fails
        try {
          console.log('[AdminLayout] Trying HTML5 Audio fallback');
          const beepSound = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURAJRqTg8sFuJAUwgM/z1oY5CCBrwO/knlMQCEah4fLDbiQFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURAJRqTg8sFuJAUwgM/z1oY5CCBrwO/knlMQCEah4fLDbiQFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURAJRqTg8sFuJAUwgM/z1oY5CCBrwO/knlMQ");
          beepSound.volume = 0.5;
          await beepSound.play();
          console.log('[AdminLayout] HTML5 Audio fallback played successfully');
        } catch (fallbackError) {
          console.error('[AdminLayout] Could not play notification sound (both methods failed):', fallbackError);
        }
      }
    };
    
    notificationSoundRef.current = playNotificationSound;
    console.log('[AdminLayout] Notification sound function initialized');
  }, []);

  // Fetch unread count only (lighter request)
  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      previousUnreadCountRef.current = -1;
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const data = await getUnreadNotificationCount(token);
      const newCount = data.unread_count || 0;
      const previousCount = previousUnreadCountRef.current;
      
      // Debug logging
      if (newCount !== previousCount) {
        console.log(`[AdminLayout] Unread count changed: ${previousCount} -> ${newCount}`);
      }
      
      // Play sound if count increased (new notification received)
      // Only play if there was a previous count >= 0 to avoid playing on initial load
      // Also play if count goes from 0 to any positive number (first notification)
      if (newCount > previousCount && previousCount >= 0 && notificationSoundRef.current) {
        console.log(`[AdminLayout] New notification detected! Count: ${previousCount} -> ${newCount}`);
        // Play sound immediately when notification arrives (don't await, fire and forget)
        notificationSoundRef.current().catch((error) => {
          console.error('[AdminLayout] Failed to play notification sound:', error);
        });
      }
      
      previousUnreadCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      // Silently handle errors for background polling - don't spam console
      // Network errors (TypeError, Failed to fetch, etc.) are common and expected during temporary connectivity issues
      const isNetworkError = error instanceof TypeError || 
                            (error.message && (
                              error.message.includes('Failed to fetch') || 
                              error.message.includes('NetworkError') ||
                              error.message.includes('Network request failed')
                            ));
      
      if (!isNetworkError) {
        console.debug('Failed to fetch unread count:', error);
      }
    }
  }, [user]);

  // Initial fetch and set up polling
  useEffect(() => {
    if (user) {
      fetchNotifications().then(() => {
        // Set initial unread count after first fetch
        const token = localStorage.getItem('auth_token');
        if (token) {
          getUnreadNotificationCount(token).then(data => {
            previousUnreadCountRef.current = data.unread_count || 0;
            setUnreadCount(data.unread_count || 0);
          }).catch(() => {});
        }
      });
      
      // Poll for unread count every 2 seconds for near real-time notifications
      notificationsIntervalRef.current = setInterval(() => {
        fetchUnreadCount();
      }, 2000);

      // Listen for custom events to trigger immediate notification refresh
      const handleNotificationRefresh = () => {
        fetchUnreadCount();
        if (notificationsOpen) {
          fetchNotifications();
        }
      };

      window.addEventListener('notification-refresh', handleNotificationRefresh);

      return () => {
        if (notificationsIntervalRef.current) {
          clearInterval(notificationsIntervalRef.current);
        }
        window.removeEventListener('notification-refresh', handleNotificationRefresh);
      };
    } else {
      setNotifications([]);
      setUnreadCount(0);
      previousUnreadCountRef.current = -1;
    }
  }, [user, fetchNotifications, fetchUnreadCount, notificationsOpen]);

  // Cleanup audio context on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (notificationsOpen && user) {
      fetchNotifications().then(() => {
        // Update previous count when opening dropdown to prevent sound on close
        const token = localStorage.getItem('auth_token');
        if (token) {
          getUnreadNotificationCount(token).then(data => {
            previousUnreadCountRef.current = data.unread_count || 0;
          }).catch(() => {});
        }
      });
    }
  }, [notificationsOpen, user, fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      // Mark as read if unread and remove from list
      if (!notification.read_at) {
        await markNotificationAsRead(notification.id, token);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      } else {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }

      // Navigate to support ticket
      if (notification.ticket_id) {
        navigate('/admin/support');
        setNotificationsOpen(false);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      await markAllNotificationsAsRead(token);
      setUnreadCount(0);
      setNotifications([]);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    if (onLogout) {
      onLogout();
    }
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-bg-primary overflow-x-hidden">
      {/* Top Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-primary via-[#8a0e24] via-secondary to-secondary backdrop-blur-xl border-b border-primary/20 shadow-lg rounded-b-none lg:rounded-b-2xl mx-0 mt-0 lg:mx-1 lg:mt-1 overflow-hidden">
        {/* Decorative overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 pointer-events-none"></div>
        <div className="relative z-10 flex items-center justify-between px-2 sm:px-3 md:px-4 lg:px-6 py-1.5 sm:py-2 md:py-2.5 lg:py-3">
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 lg:gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 sm:p-2.5 hover:bg-white/10 active:bg-white/20 rounded-lg transition-all duration-200 touch-manipulation flex items-center justify-center"
              aria-label="Toggle sidebar"
              title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            >
              <svg className={`w-4 h-4 sm:w-5 sm:h-5 text-white/90 transition-transform duration-300 ${sidebarOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <Link to="/admin" className="flex items-center gap-1.5 sm:gap-2 md:gap-2.5 lg:gap-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-white/30 blur-2xl rounded-xl group-hover:bg-white/40 transition-all opacity-50"></div>
                <div className="relative w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white/20 backdrop-blur-md rounded-lg flex items-center justify-center shadow-xl shadow-black/30 group-hover:shadow-2xl group-hover:shadow-black/40 transition-all ring-2 ring-white/40 border border-white/20 overflow-hidden">
                  <img 
                    src="/logo.png" 
                    alt="Admin Panel Logo" 
                    className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-9 lg:h-9 object-contain drop-shadow-lg group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      // Fallback to SVG icon if logo image fails to load
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'block';
                    }}
                  />
                  <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 lg:w-5 lg:h-5 text-white drop-shadow-lg hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-white tracking-tight block drop-shadow-2xl leading-tight">
                  {isCourier ? 'Courier Panel' : 'Admin Panel'}
                </span>
                <span className="text-[10px] sm:text-xs text-white/90 font-medium tracking-wide mt-0.5 drop-shadow-lg hidden sm:block">
                  {isCourier ? 'Delivery Management' : 'Control Center'}
                </span>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 lg:gap-3">
            <Link
              to="/"
              className="text-[10px] sm:text-xs md:text-sm font-semibold text-white bg-secondary/40 backdrop-blur-sm hover:bg-secondary/60 transition-all duration-300 flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 md:px-3 lg:px-4 py-1 sm:py-1.5 md:py-2 rounded-md sm:rounded-lg group border border-secondary/50 hover:border-secondary/70 shadow-lg hover:shadow-xl hover:shadow-secondary/30 hover:scale-[1.02] active:scale-[0.98]"
            >
              <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="hidden sm:inline">View Shop</span>
            </Link>
            {/* Notifications */}
            {user && (
              <div className="relative">
                <button
                  ref={notificationsButtonRef}
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="relative p-2 text-white/90 hover:text-white transition-colors rounded-full hover:bg-white/10"
                  aria-label="Notifications"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center justify-center border-2 border-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {notificationsOpen && typeof document !== 'undefined' && createPortal(
                  <>
                    <div 
                      className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm"
                      onClick={() => setNotificationsOpen(false)}
                    />
                    <div 
                      className="fixed left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 w-[calc(100vw-2rem)] max-w-sm sm:w-80 md:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-[9999] overflow-hidden animate-modal-pop max-h-[calc(100vh-120px)] sm:max-h-[80vh] flex flex-col"
                      style={{
                        top: notificationsButtonRef.current ? `${Math.max(80, notificationsButtonRef.current.getBoundingClientRect().bottom + 8)}px` : '80px',
                        ...(window.innerWidth >= 640 && notificationsButtonRef.current && {
                          right: `${window.innerWidth - notificationsButtonRef.current.getBoundingClientRect().right}px`,
                          left: 'auto',
                          transform: 'none',
                        }),
                      }}
                    >
                      {/* Header */}
                      <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-4 py-3 flex items-center justify-between border-b border-white/20">
                        <h3 className="text-base font-bold text-white">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={handleMarkAllAsRead}
                            className="text-xs text-white/90 hover:text-white font-medium underline"
                          >
                            Mark all as read
                          </button>
                        )}
                      </div>

                      {/* Notifications List */}
                      <div className="overflow-y-auto flex-1">
                        {notifications.length === 0 ? (
                          <div className="p-8 text-center">
                            <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                            <p className="text-sm text-gray-500">No notifications</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {notifications.map((notification) => (
                              <button
                                key={notification.id}
                                onClick={() => handleNotificationClick(notification)}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${!notification.read_at ? 'bg-primary' : 'bg-transparent'}`}></div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">{notification.message}</p>
                                    {notification.ticket_subject && (
                                      <p className="text-xs text-gray-500 mt-1 truncate">Ticket: {notification.ticket_subject}</p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-1">
                                      {new Date(notification.created_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      {notifications.length > 0 && (
                        <div className="border-t border-gray-200 px-4 py-2 bg-gray-50">
                          <button
                            onClick={() => {
                              navigate('/admin/support');
                              setNotificationsOpen(false);
                            }}
                            className="text-xs text-primary hover:text-primary-dark font-medium w-full text-center py-2"
                          >
                            View All Support Tickets
                          </button>
                        </div>
                      )}
                    </div>
                  </>,
                  document.body
                )}
              </div>
            )}
            <div className="relative">
              <button
                ref={userMenuButtonRef}
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-1 sm:gap-1.5 md:gap-2 px-1.5 sm:px-2 md:px-3 lg:px-4 py-1 sm:py-1.5 md:py-2 bg-gradient-to-r from-secondary/50 to-secondary-dark/50 backdrop-blur-sm rounded-md sm:rounded-lg border border-secondary/40 shadow-lg hover:shadow-xl hover:shadow-secondary/20 transition-all duration-300 hover:scale-[1.02] cursor-pointer w-full sm:w-auto"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/30 blur-lg rounded-full animate-pulse"></div>
                  <div className="relative w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9 bg-gradient-to-br from-primary via-primary to-primary-dark rounded-full flex items-center justify-center text-white text-[10px] sm:text-xs md:text-sm font-bold shadow-md ring-2 ring-white/40">
                    {(user?.name || user?.email || 'A').charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="flex flex-col min-w-0 sm:min-w-[80px] md:min-w-[100px] hidden sm:flex">
                  <span className="text-[10px] sm:text-xs md:text-sm font-bold text-white leading-tight drop-shadow-md truncate">{user?.name || user?.email}</span>
                  <span className="text-[9px] sm:text-[10px] md:text-xs text-white font-bold mt-0.5 bg-primary backdrop-blur-sm px-1 sm:px-1.5 md:px-2 py-0.5 rounded-md inline-block border border-primary-dark/50 shadow-md">{user?.role?.display_name || 'Admin'}</span>
                </div>
                <svg className="hidden sm:block w-3 h-3 ml-1 text-white/90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* User Menu Dropdown - Rendered via Portal */}
              {userMenuOpen && typeof document !== 'undefined' && createPortal(
                <>
                  <div 
                    className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div 
                    className="fixed w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] overflow-hidden"
                    style={{
                      top: userMenuButtonRef.current ? `${userMenuButtonRef.current.getBoundingClientRect().bottom + 8}px` : 'auto',
                      right: userMenuButtonRef.current ? `${window.innerWidth - userMenuButtonRef.current.getBoundingClientRect().right}px` : '16px',
                    }}
                  >
                    <div className="py-2">
                      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-primary/5 to-secondary/5">
                        <p className="text-sm font-bold text-gray-900">{user?.name || 'User'}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{user?.email}</p>
                        {user?.role && (
                          <span className="inline-block mt-2 text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded-md border border-primary/20">
                            {user.role.display_name}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          handleLogout();
                          setUserMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                      </button>
                    </div>
                  </div>
                </>,
                document.body
              )}
            </div>
            <button
              onClick={handleLogout}
              className="px-1.5 sm:px-2 md:px-3 lg:px-4 py-1 sm:py-1.5 md:py-2 bg-gradient-to-r from-primary/80 via-primary to-primary-dark/90 backdrop-blur-sm text-white rounded-md sm:rounded-lg hover:from-primary hover:via-primary-dark hover:to-primary-dark transition-all duration-300 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/50 text-[10px] sm:text-xs md:text-sm font-semibold flex items-center gap-1 sm:gap-1.5 hover:scale-[1.02] active:scale-[0.98] border border-primary/40 hover:border-primary/60"
            >
              <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="relative w-full pt-[48px] sm:pt-[58px] md:pt-[88px] lg:pt-[calc(113px-4px)]">
        <div className="flex w-full h-[calc(100vh-48px)] sm:h-[calc(100vh-58px)] md:h-[calc(100vh-88px)] lg:h-[calc(100vh-109px)]">
          {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } fixed lg:fixed z-30 w-48 sm:w-52 md:w-44 lg:w-64 xl:w-72 flex-shrink-0 h-[calc(100vh-48px)] sm:h-[calc(100vh-58px)] md:h-[calc(100vh-88px)] lg:h-[calc(100vh-109px)] xl:h-[calc(100vh-109px)] max-h-[calc(100vh-48px)] sm:max-h-[calc(100vh-58px)] md:max-h-[calc(100vh-88px)] lg:max-h-[calc(100vh-109px)] xl:max-h-[calc(100vh-109px)] backdrop-blur-xl border border-secondary/40 shadow-xl transition-all duration-300 rounded-none sm:rounded-lg md:rounded-xl lg:rounded-2xl top-[48px] sm:top-[58px] md:top-[88px] lg:top-[calc(113px-4px)] xl:top-[calc(113px-4px)] left-0 flex flex-col overflow-hidden`}
        >
          {/* Red base layer */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-primary/30 to-primary-dark/40 rounded-2xl"></div>
          
          {/* Navy blue overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-secondary via-secondary-dark via-[#002648] to-[#001d3a] rounded-2xl opacity-90"></div>
          
          {/* Decorative gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 via-transparent to-primary/15 pointer-events-none rounded-2xl"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
          
          <nav className="relative z-10 p-1.5 sm:p-2 md:p-2 lg:p-4 xl:p-6 flex-1 overflow-y-auto min-h-0">
            <ul className="space-y-1 sm:space-y-1 md:space-y-1.5 lg:space-y-2">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`relative flex items-center gap-1 sm:gap-1.5 md:gap-1.5 lg:gap-3 xl:gap-4 px-1.5 sm:px-2 md:px-2 lg:px-4 xl:px-5 py-1 sm:py-1.5 md:py-1.5 lg:py-3 xl:py-3.5 rounded-md sm:rounded-lg md:rounded-lg lg:rounded-2xl transition-all duration-300 group ${
                        isActive
                          ? 'bg-gradient-to-r from-cyan-500/90 via-blue-500/90 to-indigo-500/90 text-white shadow-xl shadow-cyan-500/30 scale-[1.02] ring-2 ring-cyan-400/40 backdrop-blur-sm'
                          : 'text-white/70 hover:bg-white/15 hover:text-white hover:scale-[1.01] border border-transparent hover:border-white/25 backdrop-blur-sm hover:shadow-lg hover:shadow-white/10'
                      }`}
                    >
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 sm:w-0.5 md:w-0.5 lg:w-1.5 h-5 sm:h-6 md:h-6 lg:h-10 bg-gradient-to-b from-cyan-300 to-blue-400 rounded-r-full shadow-lg"></div>
                      )}
                      <span className={`text-xs sm:text-sm md:text-base lg:text-xl xl:text-2xl transition-all duration-300 ${isActive ? 'scale-110 drop-shadow-lg' : 'group-hover:scale-110'}`}>
                        {item.icon}
                      </span>
                      <span className={`font-semibold text-[10px] sm:text-[11px] md:text-xs lg:text-[14px] xl:text-[15px] tracking-wide ${isActive ? 'text-white' : 'text-white/80'}`}>
                        {item.label}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>


          {/* Main Content */}
          <main className={`flex-1 ${sidebarOpen ? 'ml-48 sm:ml-52 md:ml-44 lg:ml-64 xl:ml-72' : 'ml-0'} h-[calc(100vh-48px)] sm:h-[calc(100vh-58px)] md:h-[calc(100vh-88px)] lg:h-[calc(100vh-109px)] xl:h-[calc(100vh-109px)] max-h-[calc(100vh-48px)] sm:max-h-[calc(100vh-58px)] md:max-h-[calc(100vh-88px)] lg:max-h-[calc(100vh-109px)] xl:max-h-[calc(100vh-109px)] overflow-auto bg-bg-primary mt-0 mb-0 lg:mt-2 lg:mb-2 rounded-none lg:rounded-xl transition-all duration-300 relative z-0`}>
            <div className="w-full max-w-full lg:max-w-[1600px] mx-0 lg:mx-auto px-2 sm:px-3 md:px-3 lg:px-3 xl:px-5 2xl:px-8 py-2 sm:py-3 md:py-4 lg:py-4 xl:py-6">
              <SidebarContext.Provider value={{ sidebarOpen }}>
                {children}
              </SidebarContext.Provider>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;

